import rsvpSubRoutes from './subroutes/rsvp.routes';
import ProductRoutes from './routeNames';

import { useAuthStore } from '@/stores';
import { useBingoStore } from '../stores/bingo/bingo.store';
import Tr from '@/i18n/translation';
import { Logger } from '../middlewares/sentry';
import { useGKSStore } from '../stores/gks/gks.store';
import { useSaveDateStore } from '@/stores/saveDate/saveDate.store';
import { useRsvpStore } from '@/stores/rsvp/rsvp.store';
import { useEventStore } from '@/stores/event/event.store';
import { useStateStore } from '@/stores/state.store';

async function setupData(to, store) {
  try {
    const clientId = to.params.id;
    const { trySetupData } = store();
    await trySetupData(clientId);
  } catch (err) {
    Logger.error(err);
  }
}

function handleAppReady(to, next) {
  const { getEventsType, getDefaultType } = useStateStore();
  const clientId = to.params.id;

  if (getEventsType.length > 0 && getDefaultType !== ProductRoutes.unknow) {
    next(
      Tr.i18nRoute({
        name: getDefaultType || getEventsType[0],
        params: { id: clientId },
      }),
    );
  } else {
    next();
  }
}

function setNextRoute(to, next) {
  const { getEventsType, getDefaultType, isMultiLang, getDefaultLang } =
    useStateStore();
  const clientId = to.params.id;
  const locale = to.params.locale;

  if (!isMultiLang && locale !== getDefaultLang) {
    const name = to.name;
    const params = to.params;
    params.locale = getDefaultLang;
    return next(Tr.i18nRoute({ name: name, params }));
  }

  if (
    to.matched.some((route) => {
      return (
        getEventsType.includes(route.name) ||
        ProductRoutes.unknow === route.name
      );
    })
  ) {
    next();
  } else if (getEventsType.length > 0) {
    next(
      Tr.i18nRoute({
        name: getDefaultType || getEventsType[0],
        params: { id: clientId },
      }),
    );
  } else {
    next(
      Tr.i18nRoute({ name: ProductRoutes.unknow, params: { id: clientId } }),
    );
  }
}

const eventRoutes = [
  {
    path: '404',
    name: ProductRoutes.unknow,
    component: () => import('@/views/clients/404Page.vue'),
    beforeEnter: async (to, _from, next) => {
      handleAppReady(to, next);
    },
  },
  {
    path: 'rsvp',
    component: () => import('@/views/clients/RsvpView.vue'),
    children: rsvpSubRoutes,
    beforeEnter: async (to) => {
      await setupData(to, useRsvpStore);
    },
  },
  {
    path: 'save-date',
    name: ProductRoutes.saveDate,
    component: () => import('@/views/clients/SaveDateView.vue'),
    beforeEnter: async (to) => {
      await setupData(to, useSaveDateStore);
    },
  },
  {
    path: 'event',
    name: ProductRoutes.event,
    component: () => import('@/views/clients/EventView.vue'),
    beforeEnter: async (to) => {
      await setupData(to, useEventStore);
    },
  },
  {
    path: 'sender',
    name: ProductRoutes.sender,
    component: () => import('@/views/clients/SenderView.vue'),
    beforeEnter: async (to) => {
      await setupData(to, useEventStore);
    },
  },
  {
    path: 'gks',
    name: ProductRoutes.gks,
    component: () => import('@/views/clients/GKSView.vue'),
    beforeEnter: async (to) => {
      await setupData(to, useGKSStore);
    },
  },
  {
    path: 'bingo',
    name: ProductRoutes.bingo,
    component: () => import('@/views/clients/BingoView.vue'),
    beforeEnter: async (to) => {
      await setupData(to, useBingoStore);
    },
  },
  {
    path: 'gallery',
    name: ProductRoutes.gallery,
    component: () => import('@/views/clients/GalleryView.vue'),
    beforeEnter: async (to) => {
      await setupData(to, useEventStore);
    },
  },
  {
    path: 'video-gallery',
    name: ProductRoutes.videoGallery,
    component: () => import('@/views/clients/VideoGalleryView.vue'),
    beforeEnter: async (to) => {
      await setupData(to, useEventStore);
    },
  },
  {
    path: 'page/:page',
    name: ProductRoutes.additionalPage,
    component: () => import('@/views/clients/AdditionalPageView.vue'),
    beforeEnter: async (to, _from, next) => {
      await setupData(to, useEventStore);

      const clientId = to.params.id;
      const pageId = to.params.page;
      const eventStore = useEventStore();

      if (
        pageId >= 0 &&
        eventStore.getAdditionalSites().common.length > pageId
      ) {
        next();
      } else {
        next(Tr.i18nRoute({ name: 'Event', params: { id: clientId } }));
      }
    },
  },
];

export default {
  path: 'clients',
  redirect: Tr.i18nRoute({ name: 'Home' }),
  children: [
    {
      path: ':id',
      name: 'ClientId',
      component: () => import('@/views/ClientsView.vue'),
      beforeEnter: async (to, _from, next) => {
        await setupData(to, useAuthStore);
        await setupData(to, useStateStore);
        setNextRoute(to, next);
      },
      children: [
        ...eventRoutes,
        {
          path: ':pathMatch(.*)*',
          beforeEnter: async (to, _from, next) => {
            setNextRoute(to, next);
          },
          redirect: Tr.i18nRoute({ name: 'Home' }),
        },
      ],
    },
  ],
};
